import { io } from "socket.io-client";
import { API_URL } from "./api";
import { getAuthItem } from "../utils/authStorage";
import { toast } from "react-hot-toast";

// `API_URL` in `api.js` may include the `/api/` path. The socket server
// typically listens on the site root. Strip a trailing `/api` when building
// the socket host so we connect to `https://admin.pacehrm.com` instead of
// `https://admin.pacehrm.com/api`.
const SOCKET_URL = API_URL.replace(/\/api\/?$/, "");

// Build auth payload from the current token in localStorage so the server
// can validate the client during the handshake.
const buildAuth = () => ({ token: getAuthItem("token") });

// Create a shared socket instance with explicit options suitable for Hostinger
// VPS setups. We include `polling` as a fallback so the client gracefully
// falls back when WebSocket handshake fails.
export const socket = io(SOCKET_URL, {
  path: "/socket.io",
  transports: ["websocket", "polling"],
  auth: buildAuth(),
  autoConnect: true,
  reconnection: true,
  reconnectionAttempts: Infinity,
  reconnectionDelay: 1000,
  reconnectionDelayMax: 5000,
  timeout: 20000,
});

// Track whether we've already switched to polling fallback to avoid loops
let triedPollingFallback = false;

// Handle authentication/connect errors and show a toast for visibility
socket.on("connect_error", (err) => {
  try {
    const msg = err?.message || "WebSocket connection failed";
    const dataMsg = err?.data?.message || err?.data || null;
    // Warn rather than throwing to avoid noisy stack traces in production
    console.warn(
      "WebSocket connect_error:",
      msg,
      dataMsg ? `| data: ${dataMsg}` : ""
    );
    // If the error indicates a websocket handshake/transport failure and we
    // haven't tried polling yet, attempt a graceful fallback to polling-only.
    const indicatesWebsocketFailure =
      String(msg).toLowerCase().includes("websocket") ||
      String(dataMsg || "")
        .toLowerCase()
        .includes("websocket") ||
      String(msg).toLowerCase().includes("handshake") ||
      (err && err.transport === "websocket");

    if (!triedPollingFallback && indicatesWebsocketFailure) {
      try {
        console.info(
          "WebSocket handshake failed — switching to polling fallback"
        );
        triedPollingFallback = true;
        // Update manager options to prefer polling only and reconnect.
        if (socket && socket.io && socket.io.opts) {
          socket.io.opts.transports = ["polling"];
        }
        // Force a reconnect so the new transport is used.
        if (socket.connected) socket.disconnect();
        socket.connect();
      } catch (e) {
        console.error("Error while attempting polling fallback:", e);
      }
    }
  } catch (e) {
    console.error("Error handling connect_error:", e);
  }
});

// Informational connect/disconnect handlers
socket.on("connect", () => {
  try {
    //toast.success("WebSocket connected");
    console.info("WebSocket connected");
  } catch (e) {
    console.error("Error showing connect toast:", e);
  }
});

socket.on("disconnect", (reason) => {
  try {
    console.warn("WebSocket disconnected:", reason);
    // notify user non-intrusively
    //toast(`WebSocket disconnected: ${reason}`, { icon: "⚠️" });
  } catch (e) {
    console.error("Error showing disconnect toast:", e);
  }
});

// Emit lightweight logs when reconnect attempts happen so operators can see
// the reconnection flow without printing full error stacks.
socket.on("reconnect_attempt", (attempt) => {
  try {
    console.info(`WebSocket reconnect attempt #${attempt}`);
  } catch (e) {
    console.error("Error handling reconnect_attempt:", e);
  }
});

socket.on("reconnect_failed", () => {
  try {
    console.error("WebSocket reconnection failed after multiple attempts");
  } catch (e) {
    console.error("Error handling reconnect_failed:", e);
  }
});

// Helper to reconnect socket with current token
export function reconnectSocket() {
  try {
    const token = getAuthItem("token");
    socket.auth = { token };
    if (socket.connected) {
      socket.disconnect();
    }
    socket.connect();
    console.info("WebSocket reconnect attempted with new token");
  } catch (err) {
    console.error("Error during websocket reconnect:", err);
  }
}

export function isSocketConnected() {
  return !!(socket && socket.connected);
}

// Listen for token changes in other tabs/windows and reconnect the socket
window.addEventListener("storage", (e) => {
  if (e.key === "token") {
    console.info("Token changed in storage, reconnecting socket...");
    reconnectSocket();
  }
});

export default socket;
